import React, { useState, useEffect } from 'react';
import {
  MessageCircle,
  Settings,
  Zap,
  FileText,
  AlertCircle,
  Plus,
  Trash2,
  RefreshCw,
  Eye,
  EyeOff,
  TestTube,
  Save,
  Clock,
  CheckCircle,
  XCircle,
  Activity
} from 'lucide-react';
import { toast } from 'react-toastify';
import aiSensyService from '../../../services/integrations/aiSensyService';
import { aiSensyAutomationService } from '../../../services/integrations/automationService';
import AutomationRuleModal from '../../../components/Automation/AutomationRuleModal';

interface Template {
  id: string;
  element_name?: string; // From backend database
  name?: string; // From Facebook Graph API
  status: string;
  category: string;
  language: string;
  parameter_format?: string; // From Facebook Graph API
  parameters?: any[]; // Backend processed parameters
  components?: Array<{
    type: string;
    text?: string;
    example?: {
      body_text?: string[][];
    };
  }>; // From Facebook Graph API
  content?: string; // Fallback content
  body?: string;
  headerType?: string;
  parameter_mapping?: Record<string, string> | null;
}

interface TriggerRule {
  id?: string;
  trigger_name?: string;
  name?: string; // Fallback
  trigger_event?: 'new_lead' | 'status_change' | 'custom';
  triggerType?: string; // Fallback
  fromStatus?: string;
  toStatus?: string;
  template_id?: string;
  templateId?: string; // Fallback
  parameter_mapping?: Record<string, string>;
  parameterMapping?: Record<string, string>; // Fallback
  is_active?: boolean;
  isActive?: boolean; // Fallback
  trigger_conditions?: any;
  channel?: string;
  priority?: number;
}

const AiSensySettings: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'config' | 'templates' | 'automation' | 'logs'>('config');
  const [, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showApiKey, setShowApiKey] = useState(false);
  const [templates, setTemplates] = useState<Template[]>([]);
  const [triggers, setTriggers] = useState<TriggerRule[]>([]);
  const [isSyncing, setIsSyncing] = useState(false);
  const [showTriggerSlider, setShowTriggerSlider] = useState(false);

  // Automation Rule Creation State (same as WATI)
  const [crmColumns, setCrmColumns] = useState<any[]>([]);
  const [statusOptions, setStatusOptions] = useState<any[]>([]);
  const [editingTrigger, setEditingTrigger] = useState<TriggerRule | null>(null);
  const [deleteConfirmation, setDeleteConfirmation] = useState<{show: boolean; trigger: any | null}>({show: false, trigger: null});

  // Template-level parameter mapping state
  const [expandedMappingTemplate, setExpandedMappingTemplate] = useState<string | null>(null);
  const [templateMappings, setTemplateMappings] = useState<Record<string, Record<string, string>>>({});
  const [savingMapping, setSavingMapping] = useState<string | null>(null);

  // Configuration State
  const [config, setConfig] = useState<{
    apiKey: string;
    userName: string;
    isActive: boolean;
    webhookUrl: string;
    facebookConnected: boolean;
    wabaId: string;
    selectedWaba: any;
  }>({
    apiKey: '',
    userName: '',
    isActive: false,
    webhookUrl: '',
    facebookConnected: false,
    wabaId: '',
    selectedWaba: null
  });

  // Facebook OAuth State - 3-step flow
  const [businesses, setBusinesses] = useState<any[]>([]);
  const [wabas, setWabas] = useState<any[]>([]);
  const [isConnectingFacebook] = useState(false);
  const [showBusinessSelector, setShowBusinessSelector] = useState(false);
  const [showWabaSelector, setShowWabaSelector] = useState(false);
  const [facebookAuthUrl, setFacebookAuthUrl] = useState('');
  const [isLoadingBusinesses, setIsLoadingBusinesses] = useState(false);
  const [isLoadingWabas, setIsLoadingWabas] = useState(false);
  const [selectedBusiness, setSelectedBusiness] = useState<{ id: string; name: string } | null>(null);

  // Message Logs State
  const [logs, setLogs] = useState<any[]>([]);
  const [logsFilter, setLogsFilter] = useState<'all' | 'sent' | 'failed'>('all');

  // Load configuration on mount
  useEffect(() => {
    loadConfiguration();
    generateFacebookOAuthUrl(); // Generate OAuth URL automatically on page load
    if (activeTab === 'templates') {
      loadTemplates();
      loadCrmData();
    }
    if (activeTab === 'automation') {
      loadTemplates(); // Load templates for automation modal
      loadTriggers();
      loadCrmData();
    }
    if (activeTab === 'logs') {
      loadLogs();
    }
  }, [activeTab]);

  // Generate Facebook OAuth URL automatically
  const generateFacebookOAuthUrl = () => {
    try {
      console.log('🔗 Auto-generating Facebook OAuth URL...');

      // Get client ID from localStorage (set during login)
      const userDataWrapper = JSON.parse(localStorage.getItem('crm_user') || '{}');
      // Handle nested data structure: {data: {client_id_fk: ...}}
      const userData = userDataWrapper.data || userDataWrapper;
      const userClientId = userData.client_id_fk;

      console.log('📝 User data from localStorage:', {
        hasUserData: !!userData,
        userClientId,
        fullUserData: userData
      });

      if (!userClientId) {
        console.error('❌ No user client ID found in localStorage');
        console.log('Available localStorage keys:', Object.keys(localStorage));
        console.log('crm_user data:', localStorage.getItem('crm_user'));

        // Don't generate URL without proper client ID
        setFacebookAuthUrl('');
        return;
      }

      // Use AISENSY Facebook App ID from environment variable
      const clientId = import.meta.env.VITE_AISENSY_FACEBOOK_APP_ID || '1260613974509917';
      const backendUrl = import.meta.env.VITE_BACKEND_URL || 'http://localhost:8000';
      const redirectUri = encodeURIComponent(`${backendUrl}/api/facebook-oauth/callback`);
      const state = `aisensy_${userClientId}_${Date.now()}`; // Include client ID in state
      const scopes = 'business_management,whatsapp_business_management,whatsapp_business_messaging';

      console.log('🔗 Generating Facebook OAuth URL for AiSensy', {
        facebookAppId: clientId,
        userClientId,
        backendUrl,
        redirectUri: `${backendUrl}/api/facebook-oauth/callback`,
        state,
        scopes
      });

      // Facebook OAuth URL with WhatsApp Business permissions
      const generatedUrl = `https://www.facebook.com/v23.0/dialog/oauth?client_id=${clientId}&redirect_uri=${redirectUri}&state=${state}&scope=${scopes}`;

      console.log('🚀 Facebook OAuth URL:', generatedUrl);

      // Display the URL on page
      setFacebookAuthUrl(generatedUrl);
      console.log('✅ Facebook auth URL state updated');
    } catch (error) {
      console.error('❌ Error generating Facebook OAuth URL:', error);
    }
  };

  // Handle OAuth callback on page load
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const facebookConnected = urlParams.get('facebook_connected');
    const error = urlParams.get('error');

    if (error) {
      toast.error(`Facebook connection failed: ${error}`);
      // Clean up URL
      window.history.replaceState({}, document.title, window.location.pathname);
    } else if (facebookConnected === 'true') {
      toast.success('Facebook connected successfully!');
      loadConfiguration(); // This will show the WABA if already selected
      // Clean up URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  const loadConfiguration = async () => {
    setIsLoading(true);
    try {
      const response = await aiSensyService.getSettings();
      console.log('🔍 Configuration loaded:', response.data);
      if (response.success && response.data) {
        // Check if Facebook is connected by looking for user_name in config (set during OAuth)
        const isFacebookConnected = !!(response.data.config?.user_name && response.data.config?.connected_at);
        // Get WABA ID and business ID from database columns
        const currentWabaId = response.data.waba_id || '';
        const currentBusinessId = response.data.business_id || '';
        const currentBusinessName = response.data.business_name || '';
        const currentWabaName = response.data.waba_name || '';

        console.log('🔍 Facebook connection check:', {
          isFacebookConnected,
          currentBusinessId,
          currentBusinessName,
          currentWabaId,
          currentWabaName
        });

        setConfig({
          apiKey: response.data.credentials?.apiKey || '',
          userName: response.data.credentials?.userName || '',
          isActive: response.data.is_active || false,
          webhookUrl: response.data.config?.webhookUrl || '',
          facebookConnected: isFacebookConnected,
          wabaId: currentWabaId,
          selectedWaba: currentWabaId ? { id: currentWabaId, name: currentWabaName || 'Selected WABA' } : null
        });

        // Set selected business if exists
        if (currentBusinessId) {
          setSelectedBusiness({ id: currentBusinessId, name: currentBusinessName });
        }
      }
    } catch (error) {
      toast.error('Failed to load configuration');
    } finally {
      setIsLoading(false);
    }
  };

  const loadTemplates = async () => {
    try {
      console.log('🔄 Loading templates from database...');
      const response = await aiSensyService.getTemplates();
      if (response.success && response.data) {
        console.log(`📋 Loaded ${response.data.length} templates:`, response.data.slice(0, 3));

        // Process templates to extract headerType from metadata
        const processedTemplates = response.data.map((template: any) => {
          // Extract header type from metadata.components
          let headerType: string | undefined;
          const components = template.metadata?.components || template.components || [];
          const headerComponent = components.find((comp: any) => comp.type === 'HEADER');

          if (headerComponent && headerComponent.format) {
            headerType = headerComponent.format; // IMAGE, VIDEO, DOCUMENT, or TEXT
          }

          // Extract body text for preview
          const bodyComponent = components.find((comp: any) => comp.type === 'BODY');
          const bodyText = bodyComponent?.text || template.body;

          return {
            ...template,
            headerType,
            body: bodyText,
            content: bodyText // Fallback for preview
          };
        });

        setTemplates(processedTemplates);
        // Seed editing state from saved mappings
        const initial: Record<string, Record<string, string>> = {};
        processedTemplates.forEach((t: Template) => {
          initial[t.id] = t.parameter_mapping ? { ...t.parameter_mapping } : {};
        });
        setTemplateMappings(initial);
      } else {
        console.log('📋 No templates found');
        setTemplates([]);
      }
    } catch (error) {
      console.error('❌ Failed to load templates:', error);
      toast.error('Failed to load templates');
      setTemplates([]);
    }
  };

  const loadTriggers = async () => {
    try {
      console.log('🔄 Loading automation triggers...');
      const response = await aiSensyAutomationService.getTriggers();
      if (response.success && response.data) {
        console.log(`🤖 Loaded ${response.data.length} triggers:`, response.data.slice(0, 3));
        setTriggers(response.data as TriggerRule[]);
      } else {
        console.log('🤖 No triggers found');
        setTriggers([]);
      }
    } catch (error) {
      console.error('❌ Failed to load automation rules:', error);
      toast.error('Failed to load automation rules');
      setTriggers([]);
    }
  };

  const loadLogs = async () => {
    try {
      console.log('🔄 Loading message logs...');
      const response = await aiSensyService.getLogs(logsFilter);
      if (response.success && response.data) {
        console.log(`📝 Loaded ${response.data.messages?.length || 0} logs`);
        setLogs(response.data.messages || []);
      } else {
        console.log('📝 No logs found');
        setLogs([]);
      }
    } catch (error) {
      console.error('❌ Failed to load message logs:', error);
      toast.error('Failed to load message logs');
      setLogs([]);
    }
  };

  const loadCrmData = async () => {
    try {
      const [columnsResponse, statusResponse] = await Promise.all([
        aiSensyAutomationService.getCrmLeadColumns(),
        aiSensyAutomationService.getStatusOptions()
      ]);
      setCrmColumns(columnsResponse.data || []);
      setStatusOptions(statusResponse.data || []);
    } catch (error) {
      console.error('Error loading CRM data:', error);
      setCrmColumns([]);
      setStatusOptions([]);
    }
  };

  const handleSaveTemplateMapping = async (templateId: string) => {
    setSavingMapping(templateId);
    try {
      const mapping = templateMappings[templateId] || {};
      const result = await aiSensyService.saveTemplateParameterMapping(templateId, mapping);
      if (result.success) {
        toast.success('Parameter mapping saved');
        setExpandedMappingTemplate(null);
      } else {
        toast.error(result.message || 'Failed to save mapping');
      }
    } catch (error) {
      toast.error('Failed to save mapping');
    } finally {
      setSavingMapping(null);
    }
  };

  const handleTriggerSave = async (triggerData: any) => {
    try {
      if (editingTrigger && editingTrigger.id) {
        await aiSensyAutomationService.updateTrigger(editingTrigger.id, triggerData);
        toast.success('Automation rule updated successfully');
      } else {
        await aiSensyAutomationService.createTrigger(triggerData);
        toast.success('Automation rule created successfully');
      }

      setShowTriggerSlider(false);
      setEditingTrigger(null);
      loadTriggers(); // Reload triggers
    } catch (error) {
      console.error('Error saving trigger:', error);
      toast.error('Failed to save automation rule');
    }
  };

  const handleEditTrigger = (trigger: any) => {
    setEditingTrigger(trigger);
    setShowTriggerSlider(true);
  };

  const handleDeleteTrigger = (trigger: any) => {
    setDeleteConfirmation({ show: true, trigger });
  };

  const confirmDeleteTrigger = async () => {
    if (!deleteConfirmation.trigger) return;

    try {
      await aiSensyAutomationService.deleteTrigger(deleteConfirmation.trigger.id);
      toast.success('Automation rule deleted successfully');
      setDeleteConfirmation({ show: false, trigger: null });
      loadTriggers(); // Reload triggers
    } catch (error) {
      console.error('Error deleting trigger:', error);
      toast.error('Failed to delete automation rule');
    }
  };

  const handleSaveConfig = async () => {
    setIsSaving(true);
    try {
      await aiSensyService.saveSettings(
        {
          apiKey: config.apiKey,
          userName: config.userName
        },
        {
          webhookUrl: config.webhookUrl,
          wabaId: config.wabaId
        },
        config.isActive
      );
      toast.success('Configuration saved successfully');
    } catch (error) {
      toast.error('Failed to save configuration');
    } finally {
      setIsSaving(false);
    }
  };

  const handleTestConnection = async () => {
    try {
      const response = await aiSensyService.testConnection();
      if (response.success) {
        toast.success('Connection successful!');
      } else {
        toast.error(response.message || 'Connection failed');
      }
    } catch (error) {
      toast.error('Connection failed. Please check your API key.');
    }
  };

  const handleFacebookConnect = () => {
    if (facebookAuthUrl) {
      // Redirect to the OAuth URL
      window.location.href = facebookAuthUrl;
    } else {
      toast.error('OAuth URL not generated. Please refresh the page.');
    }
  };

  const handleFacebookDisconnect = async () => {
    try {
      await aiSensyService.disconnectFacebook();
      setConfig(prev => ({
        ...prev,
        facebookConnected: false,
        wabaId: '',
        selectedWaba: null
      }));
      setWabas([]);
      setBusinesses([]);
      setSelectedBusiness(null);
      toast.success('Facebook disconnected successfully');
    } catch (error) {
      toast.error('Failed to disconnect Facebook');
    }
  };

  // Step 2: Load businesses
  const loadBusinesses = async () => {
    setIsLoadingBusinesses(true);
    try {
      console.log('🔄 Loading businesses from Facebook...');
      const response = await aiSensyService.getBusinesses();
      console.log('🔄 Businesses response:', response);
      if (response.success && response.data) {
        // Handle both response formats: { data: [...] } or { data: { data: [...] } }
        const businessList = Array.isArray(response.data) ? response.data : (response.data.data || []);
        console.log(`🏢 Loaded ${businessList.length} businesses`);
        setBusinesses(businessList);
        setShowBusinessSelector(businessList.length > 0);

        // If a business was already selected, set it
        const selectedBiz = response.data?.selected_business || (response as any).selected_business;
        if (selectedBiz) {
          setSelectedBusiness(selectedBiz);
        }

        toast.success(`Loaded ${businessList.length} Facebook Business accounts`);
      }
    } catch (error) {
      console.error('❌ Failed to load businesses:', error);
      toast.error('Failed to load Facebook Business accounts');
      setShowBusinessSelector(false);
    } finally {
      setIsLoadingBusinesses(false);
    }
  };

  // Step 2: Select business
  const handleBusinessSelect = async (businessId: string) => {
    try {
      const business = businesses.find((b: any) => b.id === businessId);
      if (!business) return;

      console.log('🔄 Selecting business:', business.name);
      await aiSensyService.selectBusiness(businessId, business.name);

      setSelectedBusiness({ id: businessId, name: business.name });
      setShowBusinessSelector(false);

      // Clear WABA selection since business changed
      setConfig(prev => ({
        ...prev,
        wabaId: '',
        selectedWaba: null
      }));
      setWabas([]);
      setShowWabaSelector(false);

      toast.success(`Business selected: ${business.name}`);
    } catch (error) {
      console.error('❌ Failed to select business:', error);
      toast.error('Failed to select business');
    }
  };

  // Step 3: Load WABAs for selected business
  const loadWABAs = async () => {
    if (!selectedBusiness) {
      toast.error('Please select a business first');
      return;
    }

    setIsLoadingWabas(true);
    try {
      console.log('🔄 Loading WABAs for business:', selectedBusiness.name);
      const response = await aiSensyService.getWABAs(selectedBusiness.id);
      console.log('🔄 WABAs response:', response);
      if (response.success && response.data) {
        // Handle both response formats: { data: [...] } or { data: { data: [...] } }
        const wabaList = Array.isArray(response.data) ? response.data : (response.data.data || []);
        console.log(`📱 Loaded ${wabaList.length} WABAs for business ${selectedBusiness.name}`);
        setWabas(wabaList);
        setShowWabaSelector(wabaList.length > 0);

        // If a WABA was already selected, set it
        const selectedWaba = response.data?.selected_waba || (response as any).selected_waba;
        if (selectedWaba) {
          setConfig(prev => ({
            ...prev,
            wabaId: selectedWaba.id,
            selectedWaba: selectedWaba
          }));
        }

        toast.success(`Loaded ${wabaList.length} WABAs for ${selectedBusiness.name}`);
      }
    } catch (error: any) {
      console.error('❌ Failed to load WABAs:', error);
      if (error.response?.data?.step === 'select_business') {
        toast.error('Please select a business first');
      } else {
        toast.error('Failed to load WhatsApp Business Accounts');
      }
      setShowWabaSelector(false);
    } finally {
      setIsLoadingWabas(false);
    }
  };

  // Step 3: Select WABA
  const handleWabaSelect = async (wabaId: string) => {
    try {
      const waba = wabas.find((w: any) => w.id === wabaId);
      if (!waba) return;

      console.log('🔄 Selecting WABA:', waba.name);
      await aiSensyService.selectWABA(wabaId, waba.name);

      setConfig(prev => ({
        ...prev,
        wabaId,
        selectedWaba: { id: wabaId, name: waba.name }
      }));
      setShowWabaSelector(false);

      toast.success(`WABA selected: ${waba.name}`);
    } catch (error) {
      console.error('❌ Failed to select WABA:', error);
      toast.error('Failed to select WABA');
    }
  };

  const handleSyncTemplates = async () => {
    setIsSyncing(true);
    try {
      console.log('🔄 Syncing templates from Facebook Graph API...');
      const response = await aiSensyService.syncTemplates();
      if (response.success) {
        toast.success(`Templates synchronized successfully! Synced ${response.data?.length || 0} templates`);
        await loadTemplates(); // Reload templates after sync
      } else {
        toast.error(response.message || 'Failed to sync templates');
      }
    } catch (error) {
      console.error('❌ Failed to sync templates:', error);
      toast.error('Failed to sync templates. Please check your Facebook connection and WABA selection.');
    } finally {
      setIsSyncing(false);
    }
  };

  const handleTestMessage = async (_templateId: string) => {
    try {
      // API call to send test message
      toast.success('Test message sent successfully');
    } catch (error) {
      toast.error('Failed to send test message');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="bg-brandColor p-2 rounded-lg">
                <MessageCircle className="h-6 w-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white">AiSensy Integration</h1>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  Configure WhatsApp Business automation with AiSensy
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                config.isActive
                  ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                  : 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400'
              }`}>
                {config.isActive ? 'Active' : 'Inactive'}
              </span>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="px-6">
          <div className="flex space-x-8 border-b border-gray-200 dark:border-gray-700">
            {[
              { id: 'config', label: 'Configuration', icon: Settings },
              { id: 'templates', label: 'Templates', icon: FileText },
              { id: 'automation', label: 'Automation', icon: Zap },
              { id: 'logs', label: 'Message Logs', icon: Activity }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`flex items-center space-x-2 px-1 py-3 border-b-2 transition-colors ${
                  activeTab === tab.id
                    ? 'border-brandColor text-brandColor dark:border-brandColor dark:text-brandColor'
                    : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
                }`}
              >
                <tab.icon className="h-4 w-4" />
                <span className="font-medium">{tab.label}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        {/* Configuration Tab */}
        {activeTab === 'config' && (
          <div className="max-w-4xl">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
              <div className="p-6 space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                    API Configuration
                  </h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        API Key
                      </label>
                      <div className="relative">
                        <input
                          type={showApiKey ? 'text' : 'password'}
                          value={config.apiKey}
                          onChange={(e) => setConfig({ ...config, apiKey: e.target.value })}
                          className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                          placeholder="Enter your AiSensy API key"
                        />
                        <button
                          onClick={() => setShowApiKey(!showApiKey)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                        >
                          {showApiKey ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                        </button>
                      </div>
                      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                        You can find your API key in AiSensy dashboard under Settings → API
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        User Name
                      </label>
                      <input
                        type="text"
                        value={config.userName}
                        onChange={(e) => setConfig({ ...config, userName: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                        placeholder="e.g., admin@company.com"
                      />
                    </div>

                    {/* Facebook Connection Section */}
                    <div className="border-t border-gray-200 dark:border-gray-600 pt-6">
                      <h4 className="text-md font-medium text-gray-900 dark:text-white mb-4">
                        Facebook WhatsApp Business
                      </h4>

                      {!config.facebookConnected ? (
                        <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                          {/* Login Warning */}
                          {!facebookAuthUrl && (
                            <div className="mb-3 p-3 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800">
                              <div className="flex items-center space-x-2">
                                <AlertCircle className="h-4 w-4 text-red-600 dark:text-red-400" />
                                <span className="text-sm text-red-800 dark:text-red-200 font-medium">
                                  Please log in to generate OAuth URL
                                </span>
                              </div>
                              <p className="text-xs text-red-600 dark:text-red-400 mt-1">
                                User session data not found in localStorage. Please refresh and log in again.
                              </p>
                            </div>
                          )}

                          <div className="flex items-start space-x-3">
                            <MessageCircle className="h-5 w-5 text-brandColor dark:text-brandColor mt-0.5" />
                            <div className="flex-1">
                              <p className="text-sm text-gray-800 dark:text-gray-200 mb-3">
                                Connect your Facebook account to access WhatsApp Business templates via Facebook Graph API.
                              </p>
                              <button
                                onClick={handleFacebookConnect}
                                disabled={isConnectingFacebook || !facebookAuthUrl}
                                className="flex items-center space-x-2 px-4 py-2 bg-brandColor text-white rounded-lg hover:bg-brand-hover disabled:opacity-50 transition-colors"
                              >
                                {isConnectingFacebook ? (
                                  <RefreshCw className="h-4 w-4 animate-spin" />
                                ) : (
                                  <MessageCircle className="h-4 w-4" />
                                )}
                                <span>{isConnectingFacebook ? 'Connecting...' : 'Connect Facebook'}</span>
                              </button>

                              {/* Display OAuth Scopes Info */}
                              {facebookAuthUrl && (
                                <div className="mt-4 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                                  <h5 className="text-sm font-medium text-gray-900 dark:text-white mb-2">
                                    Required Permissions:
                                  </h5>
                                  <p className="text-xs text-gray-600 dark:text-gray-400">
                                    <strong>Scopes:</strong> business_management, whatsapp_business_management, whatsapp_business_messaging
                                  </p>
                                  <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                                    These permissions allow the CRM to access your business accounts, manage WhatsApp Business templates, and send messages.
                                  </p>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="space-y-4">
                          <div className="flex items-center justify-between p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
                            <div className="flex items-center space-x-3">
                              <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400" />
                              <span className="text-sm font-medium text-green-800 dark:text-green-200">
                                Facebook Connected
                              </span>
                            </div>
                            <button
                              onClick={handleFacebookDisconnect}
                              className="text-sm text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300"
                            >
                              Disconnect
                            </button>
                          </div>

                          {/* 3-Step Flow: Business → WABA Selection */}

                          {/* Step 2: Business Selection */}
                          <div className="space-y-3">
                            <div className="flex items-center space-x-2">
                              <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                                selectedBusiness ? 'bg-green-500 text-white' : 'bg-brandColor text-white'
                              }`}>
                                {selectedBusiness ? '✓' : '2'}
                              </div>
                              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                Select Facebook Business
                              </label>
                            </div>

                            {isLoadingBusinesses && (
                              <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                                <div className="flex items-center space-x-3">
                                  <RefreshCw className="h-5 w-5 text-brandColor animate-spin" />
                                  <div>
                                    <p className="text-sm font-medium text-gray-800 dark:text-gray-200">
                                      Loading Facebook Business accounts...
                                    </p>
                                  </div>
                                </div>
                              </div>
                            )}

                            {selectedBusiness ? (
                              <div className="p-3 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center space-x-2">
                                    <CheckCircle className="h-4 w-4 text-green-600 dark:text-green-400" />
                                    <span className="text-sm font-medium text-gray-900 dark:text-white">
                                      {selectedBusiness.name}
                                    </span>
                                  </div>
                                  <button
                                    onClick={loadBusinesses}
                                    disabled={isLoadingBusinesses}
                                    className="text-xs px-3 py-1.5 bg-brandColor text-white rounded hover:bg-brand-hover disabled:opacity-50 transition-colors"
                                  >
                                    {isLoadingBusinesses ? 'Loading...' : 'Change'}
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                                <p className="text-xs text-blue-600 dark:text-blue-400 mb-3">
                                  Select which Facebook Business account to use
                                </p>
                                <button
                                  onClick={loadBusinesses}
                                  disabled={isLoadingBusinesses}
                                  className="flex items-center space-x-2 px-4 py-2 bg-brandColor text-white rounded-lg hover:bg-brand-hover disabled:opacity-50 transition-colors w-full justify-center"
                                >
                                  {isLoadingBusinesses ? (
                                    <>
                                      <RefreshCw className="h-4 w-4 animate-spin" />
                                      <span>Loading...</span>
                                    </>
                                  ) : (
                                    <>
                                      <RefreshCw className="h-4 w-4" />
                                      <span>Load Business Accounts</span>
                                    </>
                                  )}
                                </button>
                              </div>
                            )}

                            {/* Business Selector Dropdown */}
                            {showBusinessSelector && businesses.length > 0 && (
                              <div className="mt-2">
                                <select
                                  value={selectedBusiness?.id || ''}
                                  onChange={(e) => handleBusinessSelect(e.target.value)}
                                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-brandColor dark:bg-gray-700 dark:text-white"
                                >
                                  <option value="">Select a Business ({businesses.length} available)</option>
                                  {businesses.map((business: any) => (
                                    <option key={business.id} value={business.id}>
                                      {business.name}
                                    </option>
                                  ))}
                                </select>
                              </div>
                            )}
                          </div>

                          {/* Step 3: WABA Selection */}
                          <div className="space-y-3 mt-4">
                            <div className="flex items-center space-x-2">
                              <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                                config.wabaId ? 'bg-green-500 text-white' : selectedBusiness ? 'bg-brandColor text-white' : 'bg-gray-300 text-gray-500'
                              }`}>
                                {config.wabaId ? '✓' : '3'}
                              </div>
                              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                Select WhatsApp Business Account (WABA)
                              </label>
                            </div>

                            {isLoadingWabas && (
                              <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                                <div className="flex items-center space-x-3">
                                  <RefreshCw className="h-5 w-5 text-brandColor animate-spin" />
                                  <div>
                                    <p className="text-sm font-medium text-gray-800 dark:text-gray-200">
                                      Loading WABAs for {selectedBusiness?.name}...
                                    </p>
                                  </div>
                                </div>
                              </div>
                            )}

                            {config.wabaId ? (
                              <div className="p-3 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center space-x-2">
                                    <CheckCircle className="h-4 w-4 text-green-600 dark:text-green-400" />
                                    <span className="text-sm font-medium text-gray-900 dark:text-white">
                                      {config.selectedWaba?.name || config.wabaId}
                                    </span>
                                  </div>
                                  <button
                                    onClick={loadWABAs}
                                    disabled={isLoadingWabas || !selectedBusiness}
                                    className="text-xs px-3 py-1.5 bg-brandColor text-white rounded hover:bg-brand-hover disabled:opacity-50 transition-colors"
                                  >
                                    {isLoadingWabas ? 'Loading...' : 'Change'}
                                  </button>
                                </div>
                                <p className="text-xs text-green-600 dark:text-green-400 mt-1">
                                  ID: {config.wabaId}
                                </p>
                              </div>
                            ) : selectedBusiness ? (
                              <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                                <p className="text-xs text-blue-600 dark:text-blue-400 mb-3">
                                  Select which WABA to use for {selectedBusiness.name}
                                </p>
                                <button
                                  onClick={loadWABAs}
                                  disabled={isLoadingWabas}
                                  className="flex items-center space-x-2 px-4 py-2 bg-brandColor text-white rounded-lg hover:bg-brand-hover disabled:opacity-50 transition-colors w-full justify-center"
                                >
                                  {isLoadingWabas ? (
                                    <>
                                      <RefreshCw className="h-4 w-4 animate-spin" />
                                      <span>Loading WABAs...</span>
                                    </>
                                  ) : (
                                    <>
                                      <RefreshCw className="h-4 w-4" />
                                      <span>Load WABAs</span>
                                    </>
                                  )}
                                </button>
                              </div>
                            ) : (
                              <div className="p-3 bg-gray-100 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                                <p className="text-xs text-gray-500 dark:text-gray-400">
                                  Please select a business first (Step 2)
                                </p>
                              </div>
                            )}

                            {/* WABA Selector Dropdown */}
                            {showWabaSelector && wabas.length > 0 && (
                              <div className="mt-2">
                                <select
                                  value={config.wabaId}
                                  onChange={(e) => handleWabaSelect(e.target.value)}
                                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-brandColor dark:bg-gray-700 dark:text-white"
                                >
                                  <option value="">Select a WABA ({wabas.length} available)</option>
                                  {wabas.map((waba: any) => (
                                    <option key={waba.id} value={waba.id}>
                                      {waba.name} - {waba.id} {waba.ownership_type === 'client' ? '(Shared)' : ''}
                                    </option>
                                  ))}
                                </select>
                              </div>
                            )}

                            {showWabaSelector && wabas.length === 0 && !isLoadingWabas && (
                              <div className="mt-2 p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-200 dark:border-yellow-800">
                                <p className="text-xs text-yellow-600 dark:text-yellow-400">
                                  No WABAs found for this business. Please ensure you have WhatsApp Business API access.
                                </p>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="flex items-center justify-between py-4 px-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                      <div className="flex items-center space-x-3">
                        <input
                          type="checkbox"
                          id="active"
                          checked={config.isActive}
                          onChange={(e) => setConfig({ ...config, isActive: e.target.checked })}
                          className="h-4 w-4 text-brandColor rounded focus:ring-brandColor"
                        />
                        <label htmlFor="active" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                          Enable Integration
                        </label>
                      </div>
                      <button
                        onClick={handleTestConnection}
                        className="flex items-center space-x-2 px-4 py-2 bg-white dark:bg-gray-600 border border-gray-300 dark:border-gray-500 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-500 transition-colors"
                      >
                        <TestTube className="h-4 w-4" />
                        <span>Test Connection</span>
                      </button>
                    </div>
                  </div>
                </div>

                <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
                  <button
                    onClick={handleSaveConfig}
                    disabled={isSaving}
                    className="flex items-center space-x-2 px-6 py-2 bg-brandColor text-white rounded-lg hover:bg-brand-hover disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {isSaving ? (
                      <RefreshCw className="h-4 w-4 animate-spin" />
                    ) : (
                      <Save className="h-4 w-4" />
                    )}
                    <span>{isSaving ? 'Saving...' : 'Save Configuration'}</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Templates Tab */}
        {activeTab === 'templates' && (
          <div>
            <div className="mb-6 flex justify-between items-center">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                WhatsApp Templates
              </h3>
              <button
                onClick={handleSyncTemplates}
                disabled={isSyncing}
                className="flex items-center space-x-2 px-4 py-2 bg-brandColor text-white rounded-lg hover:bg-brand-hover disabled:opacity-50 transition-colors"
              >
                <RefreshCw className={`h-4 w-4 ${isSyncing ? 'animate-spin' : ''}`} />
                <span>{isSyncing ? 'Syncing...' : 'Sync Templates'}</span>
              </button>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
              <div className="p-6">
                {templates.length === 0 ? (
                  <div className="text-center py-12">
                    <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-500 dark:text-gray-400">No templates found</p>
                    <p className="text-sm text-gray-400 dark:text-gray-500 mt-2">
                      Click "Sync Templates" to fetch templates from AiSensy
                    </p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {templates.map((template) => (
                      <div
                        key={template.id}
                        className="border border-gray-200 dark:border-gray-700 rounded-lg p-4"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center flex-wrap gap-2">
                              <h4 className="font-medium text-gray-900 dark:text-white">
                                {template.name || template.element_name}
                              </h4>
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                template.status === 'APPROVED'
                                  ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                                  : template.status === 'PENDING'
                                  ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400'
                                  : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                              }`}>
                                {template.status}
                              </span>
                              {template.parameters && template.parameters.length > 0 && (() => {
                                const mappedCount = Object.values(templateMappings[template.id] || {}).filter(v => v).length;
                                const totalCount = template.parameters.length;
                                if (mappedCount === 0)
                                  return <span className="px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400">⚠ Not mapped</span>;
                                if (mappedCount < totalCount)
                                  return <span className="px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400">{mappedCount}/{totalCount} mapped</span>;
                                return <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">✓ All mapped</span>;
                              })()}
                            </div>
                            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                              Category: {template.category} | Language: {template.language}
                            </p>
                            {/* Template Content from Facebook Graph API */}
                            {template.components && template.components.length > 0 ? (
                              <div className="mt-3 p-3 bg-gray-50 dark:bg-gray-700/50 rounded">
                                <h5 className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">Template Content:</h5>
                                {template.components.map((component: any, index: number) => (
                                  <div key={index} className="mb-3 last:mb-0">
                                    <div className="flex items-center space-x-2 mb-1">
                                      <span className="text-xs font-medium text-brandColor dark:text-brandColor uppercase px-2 py-1 bg-gray-100 dark:bg-gray-800 rounded">
                                        {component.type}
                                      </span>
                                    </div>
                                    {component.text && (
                                      <p className="text-sm text-gray-600 dark:text-gray-300 whitespace-pre-wrap bg-white dark:bg-gray-800 p-2 rounded border">
                                        {component.text}
                                      </p>
                                    )}
                                    {/* Show example if available */}
                                    {component.example && component.example.body_text && (
                                      <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                                        <span className="font-medium">Example parameters:</span> {component.example.body_text[0]?.join(', ')}
                                      </div>
                                    )}
                                  </div>
                                ))}
                              </div>
                            ) : template.content ? (
                              <div className="mt-3 p-3 bg-gray-50 dark:bg-gray-700/50 rounded">
                                <p className="text-sm text-gray-600 dark:text-gray-300 whitespace-pre-wrap">
                                  {template.content}
                                </p>
                              </div>
                            ) : null}

                            {/* Parameters */}
                            {template.parameters && template.parameters.length > 0 && (
                              <div className="mt-3">
                                <div className="flex items-center justify-between">
                                  <p className="text-xs font-medium text-gray-600 dark:text-gray-400">
                                    Parameters: {Array.isArray(template.parameters)
                                      ? template.parameters.map((p: any) => p.name || p).join(', ')
                                      : template.parameters}
                                  </p>
                                  <button
                                    onClick={() => setExpandedMappingTemplate(
                                      expandedMappingTemplate === template.id ? null : template.id
                                    )}
                                    className="text-xs text-brandColor hover:underline flex items-center space-x-1"
                                  >
                                    <Settings className="h-3 w-3" />
                                    <span>
                                      {expandedMappingTemplate === template.id ? 'Hide' : 'Map'} Fields
                                      {template.parameter_mapping && Object.keys(template.parameter_mapping).length > 0
                                        ? ` (${Object.keys(template.parameter_mapping).length} mapped)` : ''}
                                    </span>
                                  </button>
                                </div>

                                {expandedMappingTemplate === template.id && (
                                  <div className="mt-3 p-3 bg-blue-50 dark:bg-blue-900/10 border border-blue-200 dark:border-blue-800 rounded-lg">
                                    <p className="text-xs text-blue-700 dark:text-blue-400 mb-3">
                                      Map each template parameter to a lead field. This mapping is used by drip campaigns automatically.
                                    </p>
                                    <div className="space-y-2">
                                      {(Array.isArray(template.parameters) ? template.parameters : []).map((param: any) => {
                                        const paramKey = param.name || param;
                                        return (
                                          <div key={paramKey} className="flex items-center space-x-2">
                                            <span className="text-xs font-mono bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 px-2 py-1 rounded min-w-[80px]">
                                              {`{{${paramKey}}}`}
                                            </span>
                                            <span className="text-xs text-gray-500">→</span>
                                            <select
                                              value={(templateMappings[template.id] || {})[paramKey] || ''}
                                              onChange={(e) => setTemplateMappings(prev => ({
                                                ...prev,
                                                [template.id]: {
                                                  ...(prev[template.id] || {}),
                                                  [paramKey]: e.target.value
                                                }
                                              }))}
                                              className="flex-1 text-xs border border-gray-300 dark:border-gray-600 rounded px-2 py-1 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                            >
                                              <option value="">-- Select lead field --</option>
                                              {crmColumns.map((col: any) => (
                                                <option key={col.col_id} value={col.col_name}>{col.col_label} ({col.col_name})</option>
                                              ))}
                                            </select>
                                          </div>
                                        );
                                      })}
                                    </div>
                                    <div className="mt-3 flex justify-end space-x-2">
                                      <button
                                        onClick={() => setExpandedMappingTemplate(null)}
                                        className="text-xs px-3 py-1.5 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-50 dark:hover:bg-gray-700"
                                      >
                                        Cancel
                                      </button>
                                      <button
                                        onClick={() => handleSaveTemplateMapping(template.id)}
                                        disabled={savingMapping === template.id}
                                        className="text-xs px-3 py-1.5 bg-brandColor text-white rounded hover:bg-brandColor/90 disabled:opacity-50 flex items-center space-x-1"
                                      >
                                        <Save className="h-3 w-3" />
                                        <span>{savingMapping === template.id ? 'Saving...' : 'Save Mapping'}</span>
                                      </button>
                                    </div>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                          <button
                            onClick={() => handleTestMessage(template.id)}
                            className="ml-4 px-3 py-1 text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                          >
                            Send Test
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Automation Tab */}
        {activeTab === 'automation' && (
          <div>
            <div className="mb-6 flex justify-between items-center">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Automation Rules
              </h3>
              <button
                onClick={() => setShowTriggerSlider(true)}
                className="flex items-center space-x-2 px-4 py-2 bg-brandColor text-white rounded-lg hover:bg-brand-hover transition-colors"
              >
                <Plus className="h-4 w-4" />
                <span>Add Rule</span>
              </button>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
              <div className="p-6">
                {triggers.length === 0 ? (
                  <div className="text-center py-12">
                    <Zap className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-500 dark:text-gray-400">No automation rules configured</p>
                    <p className="text-sm text-gray-400 dark:text-gray-500 mt-2">
                      Create rules to automatically send WhatsApp messages
                    </p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {triggers.map((trigger) => (
                      <div
                        key={trigger.id}
                        className="border border-gray-200 dark:border-gray-700 rounded-lg p-4"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="flex items-center space-x-3">
                              <h4 className="font-medium text-gray-900 dark:text-white">
                                {trigger.trigger_name || trigger.name}
                              </h4>
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                trigger.is_active || trigger.isActive
                                  ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                                  : 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400'
                              }`}>
                                {trigger.is_active || trigger.isActive ? 'Active' : 'Inactive'}
                              </span>
                            </div>
                            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                              Trigger: {(trigger.trigger_event || trigger.triggerType) === 'new_lead' ? 'New Lead' :
                                       (trigger.trigger_event || trigger.triggerType) === 'status_change' ? 'Status Change' : 'Custom'}
                              {(trigger.trigger_event || trigger.triggerType) === 'status_change' && (
                                <span> → {(() => {
                                  const targetStatusId = trigger.trigger_conditions?.target_status || trigger.toStatus;
                                  const statusOption = (statusOptions || []).find(opt => opt.opt_id.toString() === targetStatusId);
                                  return statusOption ? statusOption.opt_name : targetStatusId;
                                })()}</span>
                              )}
                            </p>
                            <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">
                              Template: {(() => {
                                const templateId = trigger.template_id || trigger.templateId;
                                const template = (templates || []).find(t => t.id.toString() === templateId?.toString());
                                return template ? (template.name || template.element_name) : 'Unknown Template';
                              })()}
                            </p>
                          </div>
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={() => handleEditTrigger(trigger)}
                              className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                            >
                              <Settings className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteTrigger(trigger)}
                              className="p-2 text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Logs Tab */}
        {activeTab === 'logs' && (
          <div>
            <div className="mb-6 flex justify-between items-center">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Message Logs
              </h3>
              <div className="flex items-center space-x-2">
                {['all', 'sent', 'failed'].map((filter) => (
                  <button
                    key={filter}
                    onClick={() => setLogsFilter(filter as any)}
                    className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
                      logsFilter === filter
                        ? 'bg-gray-100 text-brandColor dark:bg-gray-800 dark:text-brandColor'
                        : 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'
                    }`}
                  >
                    {filter.charAt(0).toUpperCase() + filter.slice(1)}
                  </button>
                ))}
              </div>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-200 dark:border-gray-700">
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Time
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Recipient
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Template
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Details
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {logs.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="px-6 py-12 text-center">
                          <Activity className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                          <p className="text-gray-500 dark:text-gray-400">No messages sent yet</p>
                        </td>
                      </tr>
                    ) : (
                      logs.map((log, index) => (
                        <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-300">
                            {log.created_at ? new Date(log.created_at).toLocaleString() : '-'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-300">
                            {log.recipient}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-300">
                            {log.template?.element_name || '-'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                              log.status === 'sent' || log.status === 'delivered'
                                ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                                : log.status === 'failed'
                                ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                                : 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400'
                            }`}>
                              {(log.status === 'sent' || log.status === 'delivered') && <CheckCircle className="h-3 w-3 mr-1" />}
                              {log.status === 'failed' && <XCircle className="h-3 w-3 mr-1" />}
                              {(log.status === 'pending' || log.status === 'processing') && <Clock className="h-3 w-3 mr-1" />}
                              {log.status}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400 max-w-xs truncate">
                            {log.logs?.[0]?.error_message || log.message_data?.error || '-'}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Automation Rule Modal */}
      {showTriggerSlider && (
        <AutomationRuleModal
          isOpen={showTriggerSlider}
          onClose={() => {
            setShowTriggerSlider(false);
            setEditingTrigger(null);
          }}
          trigger={editingTrigger}
          templates={templates}
          crmColumns={crmColumns}
          statusOptions={statusOptions}
          onSave={handleTriggerSave}
          provider="aisensy"
        />
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirmation.show && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center mb-4">
              <div className="flex-shrink-0">
                <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.996-.833-2.764 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                  Delete Automation Rule
                </h3>
              </div>
            </div>
            <div className="mb-4">
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Are you sure you want to delete "{deleteConfirmation.trigger?.trigger_name || deleteConfirmation.trigger?.name}"? This action cannot be undone.
              </p>
            </div>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setDeleteConfirmation({ show: false, trigger: null })}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmDeleteTrigger}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AiSensySettings;
